import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { comics, categories } from "@/mocks/comics";

const statusOptions = ["全部", "连载中", "已完结"] as const;

export default function ComicList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "全部";
  const category = searchParams.get("category") || "全部";
  const search = searchParams.get("search") || "";
  const [keyword, setKeyword] = useState(search);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: false });
  };

  const filtered = useMemo(() => {
    let list = comics;
    if (status !== "全部") list = list.filter((c) => c.status === status);
    if (category !== "全部") list = list.filter((c) => c.category === category);
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.category.includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    return list;
  }, [status, category, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("search", keyword.trim());
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "无尽漫画网漫画列表",
            description: "按连载状态与分类浏览无尽漫画网全部作品，查询更新到第几话。",
            mainEntity: {
              "@type": "ItemList",
              itemListElement: filtered.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.title,
                url: `https://comicstatus.github.io/comic/${c.id}`,
              })),
            },
          }),
        }}
      />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "漫画列表" }]} />

      <div className="mt-6 flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
          全部漫画 <span className="text-base font-normal text-secondary-500">· {filtered.length} 部作品</span>
        </h1>
        <p className="text-sm text-foreground-600">按连载状态与分类筛选，快速查询每一部漫画更新到第几话、掌握追更进度。</p>
      </div>

      {/* 状态筛选 */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {statusOptions.map((s) => (
          <button
            key={s}
            onClick={() => setParam("status", s)}
            className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
              status === s ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* 分类筛选 */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setParam("category", "全部")}
          className={`rounded-full px-3 py-1 text-xs whitespace-nowrap transition-colors ${
            category === "全部" ? "bg-accent-500 text-background-50" : "border border-background-300 text-foreground-700 hover:border-accent-400"
          }`}
        >
          全部分类
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setParam("category", cat)}
            className={`rounded-full px-3 py-1 text-xs whitespace-nowrap transition-colors ${
              category === cat ? "bg-accent-500 text-background-50" : "border border-background-300 text-foreground-700 hover:border-accent-400"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* 搜索 */}
      <form onSubmit={handleSearch} className="mt-6 flex max-w-md items-center rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400">
        <i className="ri-search-line pl-3 text-sm text-secondary-500"></i>
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索漫画名 / 标签"
          className="w-full bg-transparent px-2 py-2 text-sm outline-none placeholder:text-secondary-400"
        />
        <button type="submit" className="mr-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600" aria-label="搜索">
          <i className="ri-corner-down-left-line text-sm"></i>
        </button>
      </form>

      {/* 结果 */}
      {filtered.length > 0 ? (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {filtered.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      ) : (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
          <i className="ri-inbox-line text-4xl text-secondary-400"></i>
          <p className="mt-3 text-sm text-foreground-700">没有找到符合条件的漫画，换个筛选条件试试吧。</p>
          <button
            onClick={() => setSearchParams({})}
            className="mt-4 inline-flex items-center gap-1 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 whitespace-nowrap hover:bg-primary-600"
          >
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}